import { useTranslation } from 'next-i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { MapProvider } from '@hotelplan/components.common.map';
import { mapCoordinatesToLatLngLiteral } from '@hotelplan/components.common.map-pin';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import { useElementSize } from '@hotelplan/libs.hooks-dom';
import { sx2CssThemeFn } from '@hotelplan/libs.sx';
import { track } from '@hotelplan/libs.tracking-events';
import PdpPageContainer from 'components/domain/pdp/PdpPageContainer';
import { useGetPdpMapQuery } from 'graphql/pdp/GetPdpMap.generated';
import PdpMapLegend from './PdpMapLegend';
import PdpMapProducts from './PdpMapProducts';
import PdpMapShowNearbyControl from './PdpMapShowNearbyControl';
import usePDPHotelMapItems from './usePDPHotelMapItems';
import usePdpMapConfig from './usePdpMapConfig';

const PdpLocationHeader = styled.div(
  sx2CssThemeFn({
    display: 'flex',
    justifyContent: 'space-between',
    mb: [2, 3],
  })
);

const PdpPageTitle = styled.h2(
  sx2CssThemeFn({
    color: 'secondary',
    fontSize: ['18px', '26px'],
  })
);

const mapFormWrapperStyles = sx2CssThemeFn({
  flex: 1,
  width: '100%',
  flexWrap: 'wrap',
  alignItems: 'stretch',
  height: ['100%', 'auto'],
  '.gm-style-mtc': {
    '& *:first-child': {
      display: 'none',
    },
  },
});

const mapContainerWrapperStyles = sx2CssThemeFn({
  width: '100%',
  flexDirection: 'column',
  border: '1px #666 solid',
});

const MapFormWrapper = styled.div.attrs({ 'data-id': `map-form-wrapper` })(
  {
    display: 'flex',
  },
  mapFormWrapperStyles
);

const MapContainerWrapper = styled.div.attrs({
  'data-id': `mapContainerWrapper`,
})({ display: 'flex' }, mapContainerWrapperStyles);

const MapContainer = styled.div.attrs({
  'data-id': `mapContainer`,
})<{ isMobile: boolean }>(({ isMobile }) => ({
  background: 'bgLightGrey',
  position: 'relative',
  width: '100%',
  height: isMobile ? '450px' : '600px',
  '.gm-svpc': {
    img: {
      minWidth: '30px !important',
      minHeight: '30px !important',
    },
  },
}));

const trackMapFullScreenOpen = (language: string) => {
  track({
    event: 'enlarge_map',
    action: 'enlarge',
    label: 'Karte vergrössern auf PDP Seite',
    category: undefined,
    component: 'map',
    position: 'tab',
    language,
    _clear: true,
  });
};

const PdpMap: React.FC = () => {
  const [t, { language }] = useTranslation();
  const { id } = useObjectId();
  const { mobile } = useDeviceType();
  const cardContainerRef = useRef<HTMLDivElement>(null);

  const [showNearby, setShowNearby] = useState<boolean>(false);
  const mapConfig = usePdpMapConfig(mobile);

  const { data } = useGetPdpMapQuery({
    variables: {
      id,
    },
  });

  const { products } = usePDPHotelMapItems(showNearby, true);

  const { coordinates } = data?.pdp.map.hotel || {};
  const centerCoordinates = coordinates
    ? mapCoordinatesToLatLngLiteral(coordinates)
    : undefined;

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapSize = useElementSize(mapContainerRef);

  useEffect(() => {
    const map = mapContainerRef?.current;
    const handleFullScreenChange = () => {
      if (document.fullscreenElement) {
        trackMapFullScreenOpen(language);
      }
    };
    if (map) {
      map.addEventListener('fullscreenchange', handleFullScreenChange);
    }

    return () => {
      if (map) {
        map.removeEventListener('fullscreenchange', handleFullScreenChange);
      }
    };
  }, [mapContainerRef, language]);

  const mapProviderProps = useMemo(() => {
    const styles: React.CSSProperties = {
      position: 'absolute',
      width: `${mapSize.w}px`,
      height: `${mapSize.h}px`,
    };

    return { styles };
  }, [mapSize.h, mapSize.w]);

  return (
    <PdpPageContainer>
      <PdpLocationHeader>
        <PdpPageTitle>{t('pdp:map.title')}</PdpPageTitle>
        <PdpMapShowNearbyControl value={showNearby} onChange={setShowNearby} />
        {!mobile && <PdpMapLegend />}
      </PdpLocationHeader>
      <MapFormWrapper>
        <MapContainerWrapper>
          <MapContainer ref={mapContainerRef} isMobile={mobile}>
            <MapProvider
              {...mapProviderProps}
              zoom={mapConfig.zoom}
              center={centerCoordinates}
              options={mapConfig.options}
            >
              <PdpMapProducts
                products={products}
                cardContainerRef={cardContainerRef}
              />
            </MapProvider>
          </MapContainer>
        </MapContainerWrapper>
      </MapFormWrapper>
      {mobile && <div ref={cardContainerRef} />}
    </PdpPageContainer>
  );
};

export default PdpMap;
